'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useContext, useEffect, useRef, useState } from 'react'
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import { isSafari } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import VoiceSettings from './VoiceSettings'
import { getIdToken } from 'firebase/auth'
import {
  BsFillDice3Fill,
  BsGridFill,
  BsPersonBoundingBox,
  BsStack,
} from 'react-icons/bs'
import {
  FaCalendar,
  FaChevronDown,
  FaClipboardList,
  FaClock,
  FaExpandAlt,
  FaEyeSlash,
  FaFemale,
  FaFilm,
  FaFolder,
  FaGem,
  FaGlobeAmericas,
  FaLock,
  FaMagic,
  FaMicrochip,
  FaMicrophoneAlt,
  FaPalette,
  FaPen,
  FaPlay,
  FaPlus,
  FaRunning,
  FaTachometerAlt,
  FaTrash,
  FaUnlock,
  FaVideo,
  FaWeight,
} from 'react-icons/fa'
import { FiRefreshCw } from 'react-icons/fi'
import { GiPeach } from 'react-icons/gi'
import { HiAdjustments, HiSpeakerphone } from 'react-icons/hi'
import { IoBody, IoCloud, IoCloudOffline } from 'react-icons/io5'
import ClipLoader from 'react-spinners/ClipLoader'

import { useAuth } from '../contexts/AuthContext'
import { useUpgradeGate } from '../contexts/UpgradeGateContext'
import { CreateContext } from '../contexts/CreateContext'
import { useSessionFeed } from '../contexts/SessionFeedProvider'
import { clearCache } from '../helpers/mediaCache'
import { safeGetItem, safeSetItem } from '../helpers/safeStorage'
import { fakeProgressPct, FAKE_PROGRESS_CEILING } from '../helpers/fakeProgress'
import {
  base64ToBlob,
  isSafeCountry,
  isUrl,
  mapValueToPercentage,
  randomizeSeed,
  getVideoGemCost,
} from '../helpers/helpers'
import {
  VIDEO_V4,
  VIDEO_V5,
  VIDEO_MODEL_AUTO,
  isAutoVoice,
  isLtx,
  LTX_ASPECTS,
  LTX_CAMERA_MOVES,
  LTX_LENGTHS,
  LTX_FPS_OPTIONS,
  LTX_RESOLUTIONS,
  dialogueWordCount,
  ltxDialogueLimits,
  ltxFpsSurcharge,
  ltxResolutionSurcharge,
  ltxGemsForLength,
  normalizeAspectForVersion,
} from '../helpers/videoVersions'
import BookmarkModalContent from './BookmarkModalContent'
import CharacterModal from './CharacterModal'
import LoraModal from './LoraModal'
import Modal from './Modal'
import ModelModal from './ModelModal'
import TutorialBanner from './TutorialBanner'
import { MdLoop } from 'react-icons/md'
import { sampleImagesCreate } from '../config/staticImages'
import { PiCherriesFill } from 'react-icons/pi'
import { FaAngleDoubleRight } from 'react-icons/fa'

const InputsVideo = ({
  // Variant B reuses this panel as "Animate Settings" inside its drawer.
  // hidePromptAndActions hides the sticky generate/cost bar (generation happens
  // via the Animate button on a feed image instead).
  hidePromptAndActions = false,
  // Optional node rendered at the start of the header row (Variant B passes the
  // Image/Animate mode toggle here so it sits on one line with bookmark/reset).
  headerLeft = null,
  // animateOnly trims the panel to image-to-video: B always animates a generated
  // image, so the text prompt + the Prompt/Image "Starting Point" selector are
  // redundant and hidden. (videoStartingPoint is also forced to 'image' by B.)
  animateOnly = false,
  // textToVideoOnly makes this the B "Video" tab config: text-to-video settings
  // (model, character, attributes, orientation, quality, audio) but with the prompt
  // textarea + "Starting Point" toggle hidden (the prompt lives in the bottom
  // PromptBar). videoStartingPoint is forced to 'prompt' by B.
  textToVideoOnly = false,
  model,
  setModel,
  imageSize,
  setImageSize,
  setQuality,
  creativity,
  setCreativity,
  prompt,
  setPrompt,
  negativePrompt,
  setNegativePrompt,
  seed,
  setSeed,
  handleSubmitVideoV3,
  subscription,
  isLoading,
  lora,
  setLora,
  locked,
  setLocked,
  setFaceFix,
  chillMode,
  cooldown,
  cooldownProgress,
  setImageSrc,
  setImageHistory,
  setFilter,
  setDetails,
  customPoseImage,
  setCustomPoseImage,
  setIsLimitModalOpen,
  setErrorText,
  charImage,
  setCharImage,
  recentCharacters = [],
  setIsErrorModalOpen,
  videoRefImage,
  setVideoRefImage,
  setVideoRefLora,
  setVideoId,
  videoVersion,
  setVideoVersion,
  videoModelMode,
  setVideoModelMode,
  dialogue,
  setDialogue,
  dialogueSpeaker,
  setDialogueSpeaker,
  dialogueTone,
  setDialogueTone,
  dialogueAccent,
  setDialogueAccent,
  videoLength,
  setVideoLength,
  videoFps,
  setVideoFps,
  videoResolution,
  setVideoResolution,
  cameraMove,
  setCameraMove,
  videoQuality,
  setVideoQuality,
  videoImageSizeV2,
  setVideoImageSizeV2,
  videoProgress,
  queueLength,
  age,
  setAge,
  weight,
  setWeight,
  breasts,
  setBreasts,
  ass,
  setAss,
  showAttributes,
  setShowAttributes,
  setVideoFramerateV2,
  loop,
  setLoop,
  animateMode,
  setAnimateMode,
  audioEnabled,
  setAudioEnabled,
  animateVersion,
  setVideoRefId,
  videoSrc,
  setVideoSrc,
  videoStartingPoint,
  setVideoStartingPoint,
  setExtendModalOpen,
  setFormatTab,
  visibilityMode,
  setHasUnsavedChanges,
  lorasList,
  setLorasList,
  bookmarkModalOpen,
  setBookmarkModalOpen,
  banned,
  folderSetting,
  setFolderSetting,
  folders,
  setFolders,
  autoSave,
  setAutoSave,
  setVisibilityMode,
}) => {
  const { t } = useTranslation()
  const router = useRouter()
  const safeCountry = isSafeCountry()

  const [isLorasModalOpen, setIsLorasModalOpen] = useState(false)
  const [isModelsModalOpen, setIsModelsModalOpen] = useState(false)
  const [clearModalOpen, setClearModalOpen] = useState(false)
  const [safeWarningOpen, setSafeWarningOpen] = useState(false)
  const [animateModalOpen, setAnimateModalOpen] = useState(false)
  const [characterModalOpen, setCharacterModal] = useState(false)
  const [
    attributesFromCharacterModalOpen,
    setAttributesFromCharacterModalOpen,
  ] = useState(false)
  // The "Animate" starting-image box shows a how-to tutorial (create/find an
  // image, then animate it) — separate from the AI Character picker below.
  const [animateTutorialOpen, setAnimateTutorialOpen] = useState(false)
  const [moreSettingsOpen, setMoreSettingsOpen] = useState(
    // safeGetItem is SSR-safe (returns null) and never throws — a bare localStorage
    // access here crashes the render on iOS Safari with storage blocked.
    () => safeGetItem('createMoreSettingsOpen') === 'true',
  )

  useEffect(() => {
    safeSetItem('createMoreSettingsOpen', moreSettingsOpen ? 'true' : 'false')
  }, [moreSettingsOpen])

  // Video AI Character applies the character's appearance directly, so manual
  // body attributes would fight the selected character. Keep the sliders'
  // values intact, but force the attributes payload off while a character is set.
  const attributesLockedByCharacter = !!charImage

  useEffect(() => {
    if (attributesLockedByCharacter && showAttributes) {
      setShowAttributes(false)
    }
  }, [attributesLockedByCharacter, showAttributes, setShowAttributes])

  const handleAttributesToggle = () => {
    if (attributesLockedByCharacter) {
      setAttributesFromCharacterModalOpen(true)
      return
    }

    setShowAttributes((prev) => !prev)
  }

  const removeCharacterAndEnableAttributes = () => {
    setCharImage(null)
    setShowAttributes(true)
  }

  const [estimatedProgress, setEstimatedProgress] = useState(0)
  const fakeTimerRef = useRef(null)
  const startTimeRef = useRef(0)

  const { user, safe, isPlusOrHigher, isStudioOrHigher } = useAuth()
  const { requireFeature } = useUpgradeGate()
  const { setBFeed, bPendingIdRef, bPendingSourceRef } = useSessionFeed()
  const { resetCreateSession } = useContext(CreateContext)

  // Reset the create session. `scope` = 'all' wipes prompts, settings, attributes
  // and media; 'media' clears only the generated images/videos. The on-screen
  // feed, unsaved flag and media cache are cleared for both scopes.
  const handleReset = (scope) => {
    resetCreateSession({ scope, model: 'Real' })
    setHasUnsavedChanges(false)
    setBFeed([])
    bPendingIdRef.current = null
    bPendingSourceRef.current = null
    clearCache(user?.uid)
    setClearModalOpen(false)
  }

  useEffect(() => {
    if (videoRefImage) {
      setLora('Default')
      setPrompt('')
    }
  }, [videoRefImage])

  useEffect(() => {
    setVideoFramerateV2(25)
  }, [])

  useEffect(() => {
    if (!videoRefImage) {
      setVideoRefLora(null)
      setVideoId(null)
    } else {
      setLora('Default')
    }
  }, [videoRefImage, setVideoRefLora])

  useEffect(() => {
    if (!videoRefImage) return

    const img = new Image()
    img.src = videoRefImage
    img.onload = () => {
      const ratio = img.width / img.height
      // if within 5% of 1:1, call it square
      if (Math.abs(ratio - 1) < 0.05) {
        setVideoImageSizeV2('Square')
      }
      // wider than tall
      else if (ratio > 1) {
        setVideoImageSizeV2('Wide')
      }
      // taller than wide
      else {
        setVideoImageSizeV2('Portrait')
      }
    }
  }, [videoRefImage, setVideoImageSizeV2])

  useEffect(() => {
    if (
      videoRefImage &&
      !isUrl(videoRefImage) &&
      visibilityMode !== 'Offline'
    ) {
      setVisibilityMode('Offline')
    }
  }, [videoRefImage, visibilityMode, setVisibilityMode])

  useEffect(() => {
    const fetchFolders = async () => {
      if (!user || !moreSettingsOpen || visibilityMode === 'Offline') return

      const idToken = await getIdToken(user)

      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/get_unique_folders_private`,
          { method: 'GET', headers: { Authorization: idToken } },
        )

        if (!response.ok) throw new Error('Failed to fetch folders')

        const data = await response.json()
        if (data.folders && data.folders.length > 0) {
          setFolders([
            'Gallery',
            ...data.folders
              .filter((f) => f && f !== 'Gallery')
              .sort((a, b) =>
                a.localeCompare(b, undefined, { sensitivity: 'base' }),
              ),
          ])
        }
      } catch (error) {
        console.error('Error fetching folders:', error)
      }
    }

    fetchFolders()
  }, [user, moreSettingsOpen, visibilityMode])

  useEffect(() => {
    if (!subscription?.plan) return
    // Max (High) is a Studio+ feature; downgrade if the saved value isn't allowed.
    if (!isStudioOrHigher && videoQuality === 'High') {
      setVideoQuality('Standard')
    }
  }, [subscription?.plan, isStudioOrHigher, videoQuality, setVideoQuality])

  // ---------------------------------------------------------------- video5 (LTX 2.3) specifics
  const ltx = isLtx(videoVersion)

  // v5 renders Portrait or Wide only — a Square submit is refused before gems are charged. The
  // aspect is also set automatically from a dropped reference image, so coercing here (rather
  // than only hiding the button) is what stops a square source producing a 400.
  useEffect(() => {
    if (!ltx) return
    const allowed = normalizeAspectForVersion(videoImageSizeV2, videoVersion)
    if (allowed !== videoImageSizeV2) setVideoImageSizeV2(allowed)
  }, [ltx, videoVersion, videoImageSizeV2, setVideoImageSizeV2])

  const aspectOptions = ltx ? LTX_ASPECTS : ['Square', 'Portrait', 'Wide']

  // Safe mode zeroes every content lora and swaps in a fixed SFW scene prompt, so camera motion
  // and the pose override — both of which select a lora — do nothing there and are hidden.
  // Dialogue is NOT a lora: safe mode locks the scene, not the soundtrack, and the backend runs
  // the line through the same safe_mode_words strip every other model-bound text gets.
  const ltxExtras = ltx && safe !== true

  const pickVideoModel = (nextMode) => {
    if (nextMode === videoModelMode) return
    setVideoModelMode(nextMode)
  }

  // The word cap is a duration budget, so it follows the clip length selector live: switching
  // 6s → 10s raises the counter's denominator in the same tick the price changes.
  const dialogueLimits = ltxDialogueLimits(videoLength)
  const dialogueWords = dialogueWordCount(dialogue)
  const dialogueOverLimit = dialogueWords > dialogueLimits.words

  // Dialogue — the headline v5 feature. Rendered below Style/Poses and above the Animate box
  // on the Video tab. It is declared here as one element rather than written inline because
  // those two neighbours live inside the text-to-video branch while the feature also has to
  // appear on the image-to-video (Animate) panel — one element, two placements, no duplicate
  // markup to drift.
  //
  // LTX-2 is a joint audio-video model, so the character speaks the line lip-synced in the
  // same render: no extra pass and no extra gems.
  //
  // The box takes the WORDS SPOKEN, verbatim — the backend wraps it as `the woman says
  // "<line>"`. It is NOT an instruction field: "make her sound surprised" would be read aloud
  // as those words. Delivery is the tone selector beside it; scene direction is the prompt.
  //
  // An over-long line is CLAMPED on a word boundary by the backend, never rejected, so the
  // counter warns and never blocks.
  // mt-5 rather than relying on the neighbour: the Style/Poses grid above has no bottom margin
  // of its own (its children carry mb-1), and a grid container does not let its children's
  // margins collapse out, so without this the label sits right under the "AI art style." hint.
  // In the Animate branch this collapses against Motion's mb-4 to the same 20px, so both
  // placements get identical spacing — and it matches the my-5 rhythm of the hr separators.
  //
  // Rendered on Auto EVEN WHILE v4 IS THE RESOLVED MODEL. Gating this on `ltx` alone would make
  // Auto unreachable: Auto starts on v4, v4 hides the dialogue box, and the box is the only way
  // to type the line that selects the voice model — a closed loop with no way in.
  const dialogueSection = (ltx || videoModelMode === VIDEO_MODEL_AUTO) && (
    <div className="mt-5 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-white text-xs font-semibold flex items-center gap-1.5">
          <FaMicrophoneAlt className="text-white" size={12} />
          {t('dialogue')}
        </span>
      </div>
      <textarea
        id="dialogue"
        value={dialogue}
        onChange={(e) => setDialogue(e.target.value)}
        placeholder={t('dialoguePlaceholder')}
        maxLength={dialogueLimits.chars}
        className="w-full h-20 rounded-2xl px-4 py-3 text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition"
      />
      {/* Voice settings and the counter share one compact row under the box. Everything about
          HOW the line is said lives behind the one pill: speaker picks which character the line
          is attributed to in the prompt template (so it cannot be expressed by typing), tone is
          delivery rather than emotion, and accent colours it. They were three separate controls
          here and wrapped onto a second line in a narrow sidebar. */}
      <div className="mt-2 flex items-center justify-between gap-2">
        <VoiceSettings
          className="min-w-0"
          dialogueSpeaker={dialogueSpeaker}
          setDialogueSpeaker={setDialogueSpeaker}
          dialogueTone={dialogueTone}
          setDialogueTone={setDialogueTone}
          dialogueAccent={dialogueAccent}
          setDialogueAccent={setDialogueAccent}
        />
        <span
          className={`text-xs tabular-nums shrink-0 ${
            dialogueOverLimit ? 'text-amber-400' : 'text-gray-400'
          }`}
        >
          {dialogueWords}/{dialogueLimits.words}
        </span>
      </div>
      <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
        {dialogueOverLimit
          ? t('dialogueOverLimit', { count: dialogueLimits.words })
          : t('dialogueHint')}
      </p>
      {/* Auto silently resolves to the voice model as soon as this box is non-empty, and the
          switcher above keeps reading "Auto" — so the model AND the gem price change with
          nothing on screen saying so. That is how a stale line (this box persists across
          reloads) becomes a clip of the character reading a motion description aloud.
          `isAutoVoice` was written for exactly this and had never been called anywhere.
          Clear is the one-tap way back to v4. */}
      {isAutoVoice(videoModelMode, dialogue) && (
        <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2">
          <FaMicrophoneAlt
            className="text-amber-400 mt-0.5 shrink-0"
            size={11}
          />
          <p className="text-xs text-amber-200 leading-relaxed flex-1">
            {t('autoVoiceNotice')}
          </p>
          <button
            type="button"
            onClick={() => setDialogue('')}
            className="text-xs font-semibold text-amber-200 underline underline-offset-2 hover:text-white shrink-0"
          >
            {t('clear')}
          </button>
        </div>
      )}
    </div>
  )

  // We're "in queue" while a queue position is reported and the backend hasn't
  // sent any real progress yet. Once we leave the queue, we're processing.
  const inQueue = !!queueLength && videoProgress < 1
  const isProcessing = isLoading && !inQueue

  useEffect(() => {
    // Run the optimistic timer only while actually processing (not queued), and
    // start it from 0 at that moment. Keyed on the boolean so polling updates to
    // queueLength/videoProgress don't restart the animation mid-flight; the real
    // percentage is layered on top via Math.max in the bar width below.
    if (!isProcessing) {
      if (fakeTimerRef.current) {
        clearInterval(fakeTimerRef.current)
        fakeTimerRef.current = null
      }
      setEstimatedProgress(0)
      return
    }

    setEstimatedProgress(0)
    startTimeRef.current = Date.now()

    const EXPECTED_DURATION =
      videoVersion === 'video2' && videoQuality === 'Standard' ? 150 : 200
    const UPDATE_INTERVAL = 500
    const JITTER_MAX = 1
    fakeTimerRef.current = window.setInterval(() => {
      const elapsedSec = (Date.now() - startTimeRef.current) / 1000
      const basePct = fakeProgressPct(elapsedSec, EXPECTED_DURATION)
      const jitter = (Math.random() * 2 - 1) * JITTER_MAX
      const rawNext = Math.min(
        FAKE_PROGRESS_CEILING,
        Math.max(0, basePct + jitter),
      )

      setEstimatedProgress((prevPct) => Math.max(prevPct, rawNext))
    }, UPDATE_INTERVAL)

    return () => {
      if (fakeTimerRef.current) {
        clearInterval(fakeTimerRef.current)
        fakeTimerRef.current = null
      }
    }
  }, [isProcessing])

  return (
    <>
      <LoraModal
        isOpen={isLorasModalOpen}
        closeModal={() => setIsLorasModalOpen(false)}
        subscription={subscription}
        lora={lora}
        setLora={setLora}
        lorasList={lorasList}
        setLorasList={setLorasList}
        model={model}
        customPoseImage={customPoseImage}
        setCustomPoseImage={setCustomPoseImage}
        setIsLimitModalOpen={setIsLimitModalOpen}
        setErrorText={setErrorText}
        charImage={charImage}
        setIsErrorModalOpen={setIsErrorModalOpen}
        isVideo={true}
        user={user}
        setShowAttributes={setShowAttributes}
      />

      <ModelModal
        isOpen={isModelsModalOpen}
        closeModal={() => setIsModelsModalOpen(false)}
        subscription={subscription?.plan}
        model={model}
        setModel={setModel}
        setLora={setLora}
        setFilter={setFilter}
        disabled={['Hyperanime', 'Fur', 'K-Pop']}
        video={true}
      />

      <Modal
        isOpen={clearModalOpen}
        closeModal={() => setClearModalOpen(false)}
        title={t('resetYourSession')}
        content={
          <div className="space-y-3 text-base">
            <p>{t('chooseResetAmount')}</p>
            <ul className="space-y-2">
              <li>
                <span className="font-semibold">{t('clearContent')}</span> -{' '}
                {t('clearContentDescription')}
              </li>
              <li>
                <span className="font-semibold">{t('resetEverything')}</span> -{' '}
                {t('resetEverythingDescription')}
              </li>
            </ul>
          </div>
        }
        confirmation={t('resetEverything')}
        confirmation2={t('clearContent')}
        onConfirm={() => handleReset('all')}
        onConfirm2={() => handleReset('media')}
      />

      <Modal
        isOpen={bookmarkModalOpen}
        closeModal={() => setBookmarkModalOpen(false)}
        title={t('savedWorkflows')}
        content={
          <BookmarkModalContent
            user={user}
            model={model}
            setModel={setModel}
            prompt={prompt}
            setPrompt={setPrompt}
            negativePrompt={negativePrompt}
            setNegativePrompt={setNegativePrompt}
            lora={lora}
            setLora={setLora}
            setLorasList={setLorasList}
            creativity={creativity}
            setCreativity={setCreativity}
            imageSize={imageSize}
            setImageSize={setImageSize}
            seed={seed}
            setSeed={setSeed}
            setQuality={setQuality}
            setDetails={setDetails}
            setAge={setAge}
            setWeight={setWeight}
            setBreasts={setBreasts}
            setAss={setAss}
            setFilter={setFilter}
            setFaceFix={setFaceFix}
            cloneLoading={isLoading}
            setModalVisible={setBookmarkModalOpen}
            video={true}
          />
        }
        confirmation={t('done')}
        onConfirm={() => setBookmarkModalOpen(false)}
      />

      <Modal
        isOpen={safeWarningOpen}
        closeModal={() => {
          setSafeWarningOpen(false)
        }}
        title={t('safeModeEnabledTitle')}
        content={
          <div>
            <p className="text-white">
              {t('safeModeFeatureBlockedDescription')}
            </p>
          </div>
        }
        confirmation={t('settings')}
        onConfirm={() => router.push('/settings')}
      />

      <Modal
        isOpen={animateModalOpen}
        closeModal={() => {
          setAnimateModalOpen(false)
        }}
        title={t('animateImageEnabled')}
        content={
          <div>
            <p className="text-white">{t('animateImagePosesBlocked')}</p>
          </div>
        }
        confirmation={t('ok')}
        onConfirm={() => {}}
      />

      <Modal
        isOpen={attributesFromCharacterModalOpen}
        closeModal={() => setAttributesFromCharacterModalOpen(false)}
        title={t('attributesFromCharacterTitle')}
        content={
          <p className="text-white text-base leading-relaxed">
            {t('attributesFromCharacterVideoBody')}
          </p>
        }
        confirmation={t('keepCharacter')}
        confirmation2={t('removeCharacter')}
        onConfirm2={removeCharacterAndEnableAttributes}
      />

      <CharacterModal
        isOpen={characterModalOpen}
        closeModal={() => setCharacterModal(false)}
        recentCharacters={recentCharacters}
        charImage={charImage}
        setCharImage={setCharImage}
        onViewExplore={() => {
          setCharacterModal(false)
          router.push('/explore')
        }}
        onViewProfile={() => {
          setCharacterModal(false)
          router.push(`/profile/${user?.uid}`)
        }}
      />

      <Modal
        isOpen={animateTutorialOpen}
        closeModal={() => setAnimateTutorialOpen(false)}
        title={t('animate')}
        content={
          <div>
            <img
              src="/assets/video/animate.webp"
              alt={t('animate')}
              className="rounded-xl mb-5 w-1/2 mx-auto"
            />

            <p className="text-center text-sm mb-1">
              {t('animateTutorialFirst')}{' '}
              <span className="font-semibold text-foreground">
                {t('createAnImage')}
              </span>{' '}
              <span className="opacity-80">
                {t('animateTutorialOrExplore')}
              </span>
            </p>

            <p className="text-center text-sm leading-none">
              {t('animateTutorialThenPress')}{' '}
              <span className="inline-flex items-center gap-1 align-middle font-semibold text-foreground">
                <FaFilm className="relative top-[1px]" />
                <span>{t('animate')}</span>
              </span>{' '}
              {t('animateTutorialToBringToLife')}
            </p>

            <div className="flex justify-center gap-3 my-4">
              <button
                type="button"
                onClick={() => {
                  setFormatTab('photo')
                  setAnimateTutorialOpen(false)
                }}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white bg-[var(--main-bg-color)] transition-transform duration-200 hover:scale-[1.05]"
              >
                <FaMagic className="opacity-80" />
                {t('create')}
              </button>

              <Link
                href="/explore"
                onClick={() => setAnimateTutorialOpen(false)}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white bg-[var(--foreground-bg-color)] transition-transform duration-200 hover:scale-[1.05]"
              >
                <BsGridFill className="opacity-80" />
                {t('explore')}
              </Link>
            </div>
          </div>
        }
      />

      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmitVideoV3()
        }}
        className="w-full bg-[var(--middle-bg-color)] rounded-xl p-2"
      >
        <div className="p-2 md:min-h-screen">
          <div className={headerLeft ? 'mb-3 @container' : 'mb-3'}>
            <div
              className={`flex items-center gap-2 ${
                hidePromptAndActions && !headerLeft ? 'justify-end' : ''
              }`}
            >
              {/* When a headerLeft node is supplied (the Image/Animate toggle) it
                  fills the left of the row; otherwise the "Create" heading does. */}
              {headerLeft ? (
                <div className="flex-1 min-w-0">{headerLeft}</div>
              ) : (
                <div className="text-white font-bold text-base tracking-tight mr-auto">
                  {t('create')}
                </div>
              )}

              <button
                type="button"
                title={t('savedWorkflows')}
                aria-label={t('savedWorkflows')}
                onClick={() => {
                  setBookmarkModalOpen(true)
                }}
                className="h-9 w-9 flex items-center justify-center rounded-full bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              >
                <FaClipboardList />
              </button>
              <button
                type="button"
                onClick={() => {
                  setClearModalOpen(true)
                }}
                className="h-9 px-3 flex items-center justify-center gap-2 rounded-full bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] text-white text-xs font-semibold [@container(max-width:20rem)]:[&>span]:sr-only transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              >
                <span>{t('reset')}</span>
                <FiRefreshCw />
              </button>
            </div>
          </div>
          <hr className="my-4 border-[var(--foreground-bg-color)]" />

          <TutorialBanner
            storageKey="videoTutorialSeen"
            href="/articles/promptchan-video-tutorial"
            label={t('howToCreateVideo')}
          />

          {safe && !safeCountry && (
            <Link
              href="/settings"
              className="my-2 flex w-full items-center gap-2 rounded-2xl border border-[#7a74eb]/25 bg-[#6366f1]/10 px-3 py-2 text-xs hover:bg-[#6366f1]/20 transition"
            >
              <FaEyeSlash className="text-[#a5b4fc] shrink-0" size={12} />
              <span className="text-gray-300 flex-1">
                <span className="font-medium text-[#c7d2fe]">
                  {t('safeMode')}
                </span>
                : {t('nsfwHidden')}
              </span>
              <span className="shrink-0 text-[10px] font-semibold text-[#c7d2fe]">
                {t('update')} ›
              </span>
            </Link>
          )}

          <>
            {/* B "Video" tab (textToVideoOnly): the prompt moves to the bottom
                PromptBar and there's no source image here — the compact onboarding
                "Animate" box is rendered lower down (below Style/Poses) instead. */}
            {textToVideoOnly ? null : videoStartingPoint === 'prompt' ? (
              <div className="mb-5">
                <label className="text-white text-xs font-semibold mb-2 flex items-center gap-1.5">
                  <FaPen className="text-white" size={12} />
                  {t('prompt')}
                </label>
                <textarea
                  type="text"
                  id="prompt"
                  placeholder={t('enterYourPrompt')}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="w-full h-36 rounded-2xl px-4 py-3 text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition"
                  required
                />
                <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                  {t('enterAPrompt')}
                </p>
              </div>
            ) : (
              <div className="mb-5 relative">
                <span className="text-white text-xs font-semibold mb-2 flex items-center gap-1.5">
                  <FaFilm className="text-white" size={12} />
                  {t('animate')}
                </span>
                <div
                  onClick={
                    !user
                      ? () => router.push('/signup')
                      : // Variant B (animateOnly): this box is onboarding — show
                        // the "how to animate" tutorial to everyone. The premium
                        // gate for generating video stays in handleSubmitVideoV3.
                        animateOnly
                        ? () => setAnimateTutorialOpen(true)
                        : // Video generation is gated on Studio/Pro in
                          // handleSubmitVideoV3 — gating this box on Plus sent Plus and
                          // Premium users all the way to submit before refusing them. The
                          // old branch also opened the shared limit modal with no title and
                          // no body, so the dialog was literally blank.
                          () => {
                            if (!requireFeature('video')) return
                            setAnimateTutorialOpen(true)
                          }
                  }
                  className="relative w-full h-36 flex flex-col items-center justify-center rounded-2xl bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] hover:border-gray-400 focus:outline-none cursor-pointer overflow-hidden transition-colors"
                >
                  {videoRefImage && (
                    <>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <img
                          src={
                            isUrl(videoRefImage)
                              ? videoRefImage
                              : base64ToBlob(videoRefImage)
                          }
                          alt="Reference"
                          className="object-cover opacity-50 w-full h-full"
                        />
                      </div>
                      <button
                        type="button"
                        title={t('removeImage')}
                        aria-label={t('removeImage')}
                        className="absolute top-2 right-2 z-10 w-7 h-7 flex items-center justify-center text-xs text-white bg-red-500/90 hover:bg-red-500 rounded-full shadow"
                        onClick={(e) => {
                          e.stopPropagation()
                          setVideoRefImage(null)
                          setVideoRefId(null)
                          setVideoId(null)
                          setPrompt('')
                        }}
                      >
                        <FaTrash size={10} />
                      </button>
                    </>
                  )}
                  <p className="text-white text-sm font-semibold relative">
                    <FaFilm />
                  </p>
                </div>
                <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                  {t('selectAnAIGenerated')}
                  {!isPlusOrHigher && (
                    <>
                      {' '}
                      <Link
                        href={'/upgrade'}
                        className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                      >
                        {t('upgrade')}
                      </Link>
                    </>
                  )}
                </p>
                {videoRefImage && animateVersion === 2 && (
                  <p className="mt-2 inline-block rounded-md bg-orange-400/10 px-2 py-1 text-orange-300 text-xs font-semibold">
                    {t('animateV2StyleWarning')}
                  </p>
                )}
              </div>
            )}

            {!animateOnly && !textToVideoOnly && (
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                    <FaPlay className="text-white" size={12} />
                    {t('startingPoint')}
                  </span>
                  <div className="flex flex-1 gap-1">
                    <button
                      type="button"
                      onClick={() => setVideoStartingPoint('prompt')}
                      className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                        videoStartingPoint === 'prompt'
                          ? 'bg-white text-black'
                          : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                      }`}
                    >
                      {t('prompt')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setVideoStartingPoint('image')}
                      className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                        videoStartingPoint === 'image'
                          ? 'bg-white text-black'
                          : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                      }`}
                    >
                      {t('image')}
                    </button>
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                  {t('generateFromA')}
                </p>
              </div>
            )}

            {/* Video model. Auto is the default and picks the voice model only when there is a
                line to speak; forcing is for the two cases Auto cannot infer — a silent
                voice-model clip, or v4 while a line sits in the box. Switching reshapes the
                controls below. */}
            <div className="mb-4">
              <div className="flex items-center gap-2">
                {/* "New" sits on the section label, in the primary colour — the same inline
                    pill Inputs uses for Style. Keeping it off the buttons means they stay
                    symmetrical and the badge doesn't invert with the selected state. */}
                {/* No fixed w-24 on this row: the label carries the pill, so it sizes to
                    content instead of clipping it. */}
                <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0">
                  <FaMicrochip className="text-white" size={12} />
                  {t('videoModel')}
                  <span className="bg-[var(--main-bg-color)] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {t('new')}
                  </span>
                </span>
                {/* Auto first — it is the default and the one most users should stay on.
                    aria-pressed tracks the MODE, not the resolved version: on Auto neither
                    v4 nor v4-voice is "pressed", which is what the user actually chose. */}
                <div className="flex flex-1 gap-1">
                  {[
                    { mode: VIDEO_MODEL_AUTO, label: t('videoModelAuto') },
                    { mode: VIDEO_V4, label: 'v4' },
                    { mode: VIDEO_V5, label: 'v4-voice' },
                  ].map(({ mode, label }) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => pickVideoModel(mode)}
                      aria-pressed={videoModelMode === mode}
                      className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                        videoModelMode === mode
                          ? 'bg-white text-black'
                          : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Deliberately NOT conditional. One sentence that states the whole model
                  story — what Auto does, and what the voice model trades — so it reads the
                  same however the switcher is set. */}
              <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                {t('videoModelHint')}
              </p>
            </div>

            <hr className="my-5 border-[var(--foreground-bg-color)]" />

            {videoStartingPoint === 'prompt' ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="mb-1">
                    <label className="text-white text-xs font-semibold mb-2 flex items-center gap-1.5">
                      <FaPalette className="text-white" size={12} />
                      {t('style')}
                    </label>

                    <button
                      type="button"
                      onClick={() => setIsModelsModalOpen(true)}
                      className="group relative w-full rounded-2xl px-4 py-3 pr-9 text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] hover:border-gray-400 focus:border-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20 text-start transition-colors"
                    >
                      <span className="truncate block">
                        {t(model?.toLowerCase())}
                      </span>
                      <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-300 opacity-70 group-hover:opacity-100 transition" />
                    </button>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('chooseTheAiModel')}
                    </p>
                  </div>

                  {!safeCountry && (
                    <div className="mb-1">
                      <label className="text-white text-xs font-semibold mb-2 flex items-center gap-1.5">
                        <FaFemale className="text-white" size={12} />
                        {t('poses')}
                      </label>

                      <button
                        type="button"
                        onClick={() => {
                          if (safe !== true) {
                            setIsLorasModalOpen(true)
                          } else {
                            setSafeWarningOpen(true)
                          }
                        }}
                        className={`group relative w-full rounded-2xl px-4 py-3 pr-9 text-white border border-[var(--foreground-bg-color)] hover:border-gray-400 focus:border-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20 text-start overflow-hidden transition-colors ${
                          lora !== 'Custom' ? 'bg-[var(--middle-bg-color)]' : ''
                        }`}
                        style={
                          lora === 'Custom'
                            ? {
                                backgroundImage: `url(${customPoseImage})`,
                                backgroundSize: 'cover',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                              }
                            : {}
                        }
                      >
                        {lora === 'Custom' && (
                          <span className="absolute inset-0 bg-black/35 pointer-events-none" />
                        )}
                        <span
                          style={{
                            textShadow: '2px 2px 4px rgba(0,0,0,0.6)',
                          }}
                          className="relative truncate block"
                        >
                          {lorasList?.length > 0 ? t('multiple') : t(lora)}
                        </span>
                        <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-200 opacity-70 group-hover:opacity-100 transition" />
                      </button>
                      <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                        {t('chooseThePose')}
                      </p>
                    </div>
                  )}
                </div>

                {dialogueSection}

                {textToVideoOnly && (
                  <hr className="my-5 border-[var(--foreground-bg-color)]" />
                )}

                {/* Compact onboarding entry point for the Video (text-to-video)
                    tab — opens the "how to animate" tutorial. Shorter than the
                    image-to-video source box and sits below Style/Poses. */}
                {textToVideoOnly && (
                  <div className="mb-5 relative">
                    <span className="text-white text-xs font-semibold mb-2 flex items-center gap-1.5">
                      <FaFilm className="text-white" size={12} />
                      {t('animate')}
                    </span>
                    <button
                      type="button"
                      onClick={() => setAnimateTutorialOpen(true)}
                      className="relative w-full h-24 flex items-center justify-center rounded-2xl bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] hover:border-gray-400 focus:outline-none cursor-pointer transition-colors"
                    >
                      <FaFilm className="text-white" />
                    </button>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('selectAnAIGenerated')}
                    </p>
                  </div>
                )}

                <hr className="my-5 border-[var(--foreground-bg-color)]" />

                {user && (
                  <div className="mb-5">
                    <div className="flex items-center mb-2">
                      <label className="text-white text-sm font-semibold flex items-center gap-2">
                        <BsPersonBoundingBox className="text-white" size={12} />
                        {t('character')}
                      </label>
                      {charImage && (
                        <span className="flex items-center text-white text-[11px] ml-auto bg-[var(--foreground-bg-color)] rounded-full px-2.5 py-0.5">
                          <FaGem className="mr-1" size={10} />
                          <span className="font-semibold">+1</span>
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <div
                        title={t('pickACharacter')}
                        onClick={() => {
                          // Pro, not Plus — matches the backend's non-preset reference gate.
                          if (!requireFeature('aiCharacter')) return
                          setCharacterModal(true)
                        }}
                        className="relative w-11 h-11 flex items-center justify-center rounded-full border border-[var(--foreground-bg-color)] hover:border-gray-400 bg-[var(--middle-bg-color)] cursor-pointer transition-colors"
                      >
                        {charImage ? (
                          <>
                            <img
                              src={charImage}
                              alt="Character"
                              className="absolute inset-0 w-full h-full object-cover rounded-full"
                            />
                            <button
                              type="button"
                              title={t('removeCharacter')}
                              aria-label={t('removeCharacter')}
                              className="absolute -top-1 -right-1 z-10 w-5 h-5 flex items-center justify-center text-[10px] text-white bg-red-500/90 hover:bg-red-500 rounded-full shadow"
                              onClick={(e) => {
                                e.stopPropagation()
                                setCharImage(null)
                              }}
                            >
                              <FaTrash size={8} />
                            </button>
                          </>
                        ) : (
                          <FaPlus className="text-white text-sm" />
                        )}
                      </div>

                      <div className="flex items-center gap-1.5">
                        {sampleImagesCreate.map((image) => (
                          <div
                            key={image.id}
                            onClick={() => {
                              setCharImage(image.imageUrl)
                            }}
                            className="w-11 h-11 rounded-full border border-[var(--foreground-bg-color)] hover:border-gray-400 bg-[var(--middle-bg-color)] cursor-pointer overflow-hidden transition-colors"
                          >
                            <img
                              src={image.imageUrl}
                              alt={image.prompt}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('selectAnAi')}
                      {!isStudioOrHigher && (
                        <>
                          {' '}
                          <Link
                            href={'/upgrade'}
                            className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                          >
                            {t('upgrade')}
                          </Link>
                        </>
                      )}
                    </p>
                  </div>
                )}

                <hr className="my-5 border-[var(--foreground-bg-color)]" />

                <div className="mb-5">
                  <div className="flex items-center mb-2">
                    <span className="text-white text-sm font-semibold flex items-center gap-2">
                      <IoBody className="text-white" size={12} />
                      {t('attributes')}
                    </span>
                    <div
                      className={`ml-auto relative inline-block w-12 h-6 rounded-full transition-colors duration-200 ease-out cursor-pointer shrink-0 ${
                        showAttributes && !attributesLockedByCharacter
                          ? 'bg-[var(--main-bg-color)] ring-1 ring-inset ring-white/15'
                          : 'bg-[var(--foreground-bg-color)]'
                      }`}
                      onClick={handleAttributesToggle}
                      role="switch"
                      aria-checked={
                        showAttributes && !attributesLockedByCharacter
                      }
                      aria-disabled={attributesLockedByCharacter}
                      title={
                        attributesLockedByCharacter
                          ? t('attributesFromCharacterHint')
                          : undefined
                      }
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 inline-block w-5 h-5 rounded-full shadow transform transition-transform duration-200 ease-out ${
                          showAttributes && !attributesLockedByCharacter
                            ? 'translate-x-6 bg-white'
                            : 'translate-x-0 bg-gray-500'
                        }`}
                      />
                    </div>
                  </div>

                  <p className="text-gray-400 text-xs leading-relaxed mb-2">
                    {attributesLockedByCharacter
                      ? t('attributesFromCharacterHint')
                      : t('adjustYourCharacter')}
                  </p>

                  <div
                    className={`w-10 h-0.5 bg-[var(--secondary-bg-color)] rounded-full transition-[max-height,opacity,margin] duration-300 ease-in-out overflow-hidden mx-auto mt-2 ${
                      !showAttributes
                        ? 'max-h-40 opacity-100'
                        : 'max-h-0 opacity-0 mt-0'
                    }`}
                  ></div>

                  <div
                    className={`transition-[max-height,opacity,margin,padding] duration-300 ease-in-out px-1 overflow-hidden ${
                      showAttributes
                        ? 'opacity-100 mt-4 pt-2'
                        : 'max-h-0 opacity-0 mt-0 pt-0 pointer-events-none'
                    }`}
                  >
                    <div className="mb-4 flex items-center gap-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <FaCalendar className="text-white" size={12} />
                        {t('age')}
                      </span>
                      <input
                        type="range"
                        min={18}
                        max={80}
                        value={age}
                        onChange={(e) => setAge(parseInt(e.target.value, 10))}
                        className="slider flex-1"
                      />
                      <div className="text-white w-12 text-xs text-right shrink-0 tabular-nums">
                        {age}
                      </div>
                    </div>

                    <div className="mb-4 flex items-center gap-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <FaWeight className="text-white" size={12} />
                        {t('weight')}
                      </span>
                      <input
                        type="range"
                        min={-1}
                        max={1}
                        step={0.05}
                        value={weight}
                        onChange={(e) => setWeight(parseFloat(e.target.value))}
                        className="slider flex-1"
                      />
                      <div className="text-white w-12 text-xs text-right shrink-0 tabular-nums">
                        {mapValueToPercentage(weight)}
                      </div>
                    </div>

                    <div className="mb-4 flex items-center gap-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <PiCherriesFill className="text-white" size={12} />
                        {t('breastSize')}
                      </span>
                      <input
                        type="range"
                        min={-1}
                        max={1}
                        step={0.05}
                        value={breasts}
                        onChange={(e) => setBreasts(parseFloat(e.target.value))}
                        className="slider flex-1"
                      />
                      <div className="text-white w-12 text-xs text-right shrink-0 tabular-nums">
                        {mapValueToPercentage(breasts)}
                      </div>
                    </div>

                    <div className="mb-4 flex items-center gap-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <GiPeach className="text-white" size={12} />
                        {t('assSize')}
                      </span>
                      <input
                        type="range"
                        min={-1}
                        max={1}
                        step={0.05}
                        value={ass}
                        onChange={(e) => setAss(parseFloat(e.target.value))}
                        className="slider flex-1"
                      />
                      <div className="text-white w-12 text-xs text-right shrink-0 tabular-nums">
                        {mapValueToPercentage(ass)}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                      <FaRunning className="text-white" size={12} />
                      {t('motion')}
                    </span>
                    <div className="flex flex-1 gap-1">
                      <button
                        type="button"
                        onClick={() => setAnimateMode('auto')}
                        className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                          animateMode === 'auto'
                            ? 'bg-white text-black'
                            : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                        }`}
                      >
                        Auto
                      </button>
                      <button
                        type="button"
                        onClick={() => setAnimateMode('prompt')}
                        className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                          animateMode === 'prompt'
                            ? 'bg-white text-black'
                            : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                        }`}
                      >
                        {t('prompt')}
                      </button>
                    </div>
                  </div>
                  {/* Rendered in BOTH modes, disabled under Auto rather than unmounted. When it
                      vanished, the next textarea down was the dialogue box — so the only box
                      under a heading that reads "Motion" was the one whose contents get SPOKEN.
                      Keeping a visibly-disabled box holds the position and names the mode.

                      `required` stays keyed to prompt mode exactly as before: a disabled control
                      is skipped by constraint validation, so Auto is unaffected either way. */}
                  <textarea
                    type="text"
                    id="prompt"
                    placeholder={
                      animateMode === 'prompt'
                        ? t('describeMotionInScene')
                        : t('motionAutoPlaceholder')
                    }
                    value={animateMode === 'prompt' ? prompt : ''}
                    onChange={(e) => setPrompt(e.target.value)}
                    disabled={animateMode !== 'prompt'}
                    aria-label={t('motion')}
                    className="w-full h-20 rounded-2xl px-4 py-3 text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition disabled:cursor-not-allowed disabled:opacity-50"
                    required={animateMode === 'prompt'}
                    maxLength={180}
                  />
                  <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                    Auto = AI prompts motion. Prompt = you describe it.
                  </p>
                </div>
                {dialogueSection}
              </>
            )}

            {/* Audio is a v4 control. v5 renders its soundtrack in the same job, always, at no
                extra cost — there is nothing to toggle, and a row that only ever reads
                "Included" reads as a switch that does nothing. The fact is carried by the v5
                model hint above ("built-in sound") instead. */}
            {!ltx && (
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 flex-1">
                    <HiSpeakerphone className="text-white" size={12} />
                    {t('audio')}
                    {audioEnabled && (
                      <span className="flex items-center text-white">
                        <FaGem size={9} />
                        <span className="font-semibold text-[10px] ml-0.5">
                          +3
                        </span>
                      </span>
                    )}
                  </label>
                  <div
                    className={`relative inline-block w-12 h-6 rounded-full transition-colors duration-200 ease-out cursor-pointer shrink-0 ${
                      audioEnabled
                        ? 'bg-[var(--main-bg-color)] ring-1 ring-inset ring-white/15'
                        : 'bg-[var(--foreground-bg-color)]'
                    }`}
                    onClick={() => {
                      setAudioEnabled(!audioEnabled)
                    }}
                    role="switch"
                    aria-checked={audioEnabled}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 inline-block w-5 h-5 rounded-full shadow transform transition-transform duration-200 ease-out ${
                        audioEnabled
                          ? 'translate-x-6 bg-white'
                          : 'translate-x-0 bg-gray-500'
                      }`}
                    />
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                  {t('enableToAdd')}
                  {!isStudioOrHigher && (
                    <>
                      {' '}
                      <Link
                        href="/upgrade"
                        className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                      >
                        {t('upgrade')}
                      </Link>
                    </>
                  )}
                </p>
              </div>
            )}

            <hr className="my-5 border-[var(--foreground-bg-color)]" />

            <button
              type="button"
              onClick={() => setMoreSettingsOpen((v) => !v)}
              aria-expanded={moreSettingsOpen}
              className="w-full flex items-center justify-between gap-2 px-4 h-11 mb-4 rounded-full bg-[var(--foreground-bg-color)]/70 hover:bg-[var(--secondary-bg-color)] text-white text-sm font-semibold transition-colors active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/25"
            >
              <span className="flex items-center gap-2">
                <HiAdjustments className="text-base" />
                {t('moreSettings')}
              </span>
              <FaChevronDown
                className={`text-[10px] transition-transform duration-200 ${
                  moreSettingsOpen ? 'rotate-180' : 'rotate-0'
                }`}
              />
            </button>
            {moreSettingsOpen && (
              <>
                {/* Clip length (v5). Priced per length off measured GPU time. 15s and 20s render
                    fine but drift measurably and read as glitchy, so the ceiling is a quality
                    decision. Sits outside the starting-point branch so it renders for both
                    text-to-video and animate.

                    This is the length of ONE clip. Extend (coming) chains clips; it does not
                    replace this control.

                    Layout mirrors the AI Character section: label + gem pill on a full-width row
                    with the pill pushed right, control on the row below. The pill is ALWAYS
                    rendered — a conditional badge popped in and out and reflowed the buttons. */}
                {ltx && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5">
                        <FaClock className="text-white" size={12} />
                        {t('clipLength')}
                        <span className="bg-[var(--main-bg-color)] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                          {t('new')}
                        </span>
                      </span>
                      <span className="flex items-center text-white text-[11px] ml-auto bg-[var(--foreground-bg-color)] rounded-full px-2.5 py-0.5">
                        <FaGem className="mr-1" size={10} />
                        <span className="font-semibold">
                          {ltxGemsForLength(videoLength)}
                        </span>
                      </span>
                    </div>
                    <div className="flex gap-1">
                      {LTX_LENGTHS.map((len) => (
                        <button
                          key={len.value}
                          type="button"
                          onClick={() => setVideoLength(len.value)}
                          aria-pressed={videoLength === len.value}
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            videoLength === len.value
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          }`}
                        >
                          {len.value}s
                        </button>
                      ))}
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('clipLengthHint')}
                    </p>
                  </div>
                )}

                {/* Frame rate (v5). Its own section: 24 is what the model natively renders, 48 adds
                    a RIFE pass on the interframe pods AFTERWARDS — same render, smoother playback —
                    so it is a separate decision from how long the clip is, and it carries its own
                    cost. The pill shows that cost on its own rather than folding it into the clip
                    length total, and is always rendered ("Free" at the native rate) so switching
                    never reflows the row. */}
                {ltx && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5">
                        <FaTachometerAlt className="text-white" size={12} />
                        {t('frameRate')}
                        <span className="bg-[var(--main-bg-color)] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                          {t('new')}
                        </span>
                      </span>
                      {/* Only shown when the choice actually costs something. It sits on its own
                          label row pushed right by ml-auto, so appearing/disappearing moves nothing
                          else — unlike the earlier inline version, which reflowed the buttons. */}
                      {ltxFpsSurcharge(videoLength, videoFps) > 0 && (
                        <span className="flex items-center text-white text-[11px] ml-auto bg-[var(--foreground-bg-color)] rounded-full px-2.5 py-0.5">
                          <FaGem className="mr-1" size={10} />
                          <span className="font-semibold">
                            +{ltxFpsSurcharge(videoLength, videoFps)}
                          </span>
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {LTX_FPS_OPTIONS.map((fps) => (
                        <button
                          key={fps}
                          type="button"
                          onClick={() => setVideoFps(fps)}
                          aria-pressed={videoFps === fps}
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            videoFps === fps
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          }`}
                        >
                          {fps} {t('fps')}
                        </button>
                      ))}
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('frameRateHint')}
                    </p>
                  </div>
                )}

                {/* Render resolution (v5). Standard is the shipped 704x1280; High renders
                    960x1664 for ~73% more GPU, hence its own surcharge pill. Opt-in rather than
                    default because testing found it does not improve facial detail specifically
                    (LTX regenerates the face from a 352x640 latent either way) — it buys a
                    larger, cleaner render. Same section shape as frame rate above.
                */}
                {ltx && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5">
                        <FaExpandAlt className="text-white" size={12} />
                        {t('videoResolution')}
                      </span>
                      {ltxResolutionSurcharge(videoLength, videoResolution) >
                        0 && (
                        <span className="flex items-center text-white text-[11px] ml-auto bg-[var(--foreground-bg-color)] rounded-full px-2.5 py-0.5">
                          <FaGem className="mr-1" size={10} />
                          <span className="font-semibold">
                            +
                            {ltxResolutionSurcharge(
                              videoLength,
                              videoResolution,
                            )}
                          </span>
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {LTX_RESOLUTIONS.map((res) => (
                        <button
                          key={res.value}
                          type="button"
                          onClick={() => setVideoResolution(res.value)}
                          aria-pressed={videoResolution === res.value}
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex flex-col items-center justify-center leading-tight focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            videoResolution === res.value
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          }`}
                        >
                          <span>{t(res.labelKey)}</span>
                          <span className="text-[10px] opacity-60">
                            {res.dims}
                          </span>
                        </button>
                      ))}
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('videoResolutionHint')}
                    </p>
                  </div>
                )}

                {/* Camera motion (v5). "None" = no camera lora and nothing added to the prompt,
                    which is the right answer for most renders — so it lives here rather than in
                    the main panel. The options are exactly what the live pod image can serve:
                    camera loras are baked into that image, so this list grows with a rebuild. */}
                {ltxExtras && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <FaVideo className="text-white" size={12} />
                        {t('cameraMotion')}
                      </span>
                      <div className="flex flex-1 gap-1">
                        {LTX_CAMERA_MOVES.map((move) => (
                          <button
                            key={move.value || 'auto'}
                            type="button"
                            onClick={() => setCameraMove(move.value)}
                            className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                              (cameraMove || '') === move.value
                                ? 'bg-white text-black'
                                : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                            }`}
                          >
                            {t(move.labelKey)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {cameraMove
                        ? t('cameraMotionHintLora')
                        : t('cameraMotionHint')}
                    </p>
                  </div>
                )}

                {/* v5 has one fixed spec (704x1280, 145 frames at 24fps) — no Standard/Max tier
                    and no upscale pass, and the backend normalises both away. */}
                {!ltx && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <BsStack className="text-white" size={12} />
                        {t('quality')}
                        {videoQuality === 'High' ? (
                          <span className="flex items-center text-white">
                            <FaGem size={9} />
                            <span className="font-semibold text-[10px] ml-0.5">
                              +10
                            </span>
                          </span>
                        ) : null}
                      </span>
                      <div className="flex flex-1 gap-1">
                        <button
                          type="button"
                          onClick={() => setVideoQuality('Standard')}
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            videoQuality === 'Standard'
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          }`}
                        >
                          {t('standard')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setVideoQuality('High')}
                          disabled={!isStudioOrHigher}
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors button-glow inline-flex items-center justify-center gap-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            videoQuality === 'High'
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          } ${!isStudioOrHigher ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {t('max')}
                          {!isStudioOrHigher && <FaLock size={9} />}
                        </button>
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('selectTheDesiredOutputImageVideo')}
                      {!isStudioOrHigher && (
                        <>
                          {' '}
                          <Link
                            href="/upgrade"
                            className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                          >
                            {t('upgrade')}
                          </Link>
                        </>
                      )}
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                      <FaExpandAlt className="text-white" size={12} />
                      {t('imageSize')}
                    </span>
                    {/* Square is v4-only: the v5 workflow was tuned portrait/landscape and a
                        Square submit is refused before gems are charged. */}
                    <div className="flex flex-1 gap-1">
                      {aspectOptions.map((aspect) => (
                        <button
                          key={aspect}
                          type="button"
                          onClick={() => setVideoImageSizeV2(aspect)}
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            videoImageSizeV2 === aspect
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          }`}
                        >
                          {t(aspect.toLowerCase())}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                    {t('selectTheAspectRatio')}
                  </p>
                </div>

                {/* Playback is a v4 control — v5 has no loop mode (normalised to false). */}
                {!ltx && lora !== 'Default' && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <MdLoop className="text-white" size={12} />
                        {t('playback')}
                      </span>
                      <div className="flex flex-1 gap-1">
                        <button
                          type="button"
                          onClick={() => setLoop('Repeat')}
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            loop === 'Repeat'
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          }`}
                        >
                          {t('repeat')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setLoop('Loop')}
                          className={`flex-1 h-9 text-xs rounded-xl transition-colors inline-flex items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 ${
                            loop === 'Loop'
                              ? 'bg-white text-black'
                              : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                          }`}
                        >
                          {t('loop')}
                        </button>
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('selectTheLoop')}
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                      <BsFillDice3Fill className="text-white" size={12} />
                      {t('seed')}
                    </span>
                    <input
                      type="number"
                      pattern="\d*"
                      id="seed"
                      placeholder={t('seed')}
                      value={seed}
                      onChange={(e) => {
                        setLocked(true)
                        setSeed(e.target.value)
                      }}
                      disabled={!isPlusOrHigher}
                      className={`appearance-none rounded-xl px-3 h-9 flex-1 min-w-0 text-xs text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition ${
                        !isPlusOrHigher ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    />
                    <button
                      type="button"
                      title={t('randomizeSeed')}
                      aria-label={t('randomizeSeed')}
                      onClick={() => randomizeSeed(setSeed)}
                      disabled={!isPlusOrHigher}
                      className={`h-9 w-9 inline-flex items-center justify-center rounded-xl text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 shrink-0 ${
                        !isPlusOrHigher ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <FiRefreshCw />
                    </button>
                    <button
                      type="button"
                      title={locked ? 'Unlock seed' : 'Lock seed'}
                      aria-label={locked ? 'Unlock seed' : 'Lock seed'}
                      onClick={() => setLocked(!locked)}
                      disabled={!isPlusOrHigher}
                      className={`h-9 w-9 inline-flex items-center justify-center rounded-xl transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 shrink-0 ${
                        !isPlusOrHigher
                          ? 'opacity-50 cursor-not-allowed text-gray-200 bg-[var(--foreground-bg-color)]'
                          : locked
                            ? 'bg-white text-black'
                            : 'text-gray-200 bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] hover:text-white'
                      }`}
                    >
                      {locked ? <FaLock /> : <FaUnlock />}
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                    {t('enterASeed')}
                    {!isPlusOrHigher && (
                      <>
                        {' '}
                        <Link
                          href="/upgrade"
                          className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                        >
                          {t('upgrade')}
                        </Link>
                      </>
                    )}
                  </p>
                </div>

                <hr className="my-5 border-[var(--foreground-bg-color)]" />

                {!banned && !model.includes('Flame') && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <label className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <FaGlobeAmericas className="text-white" size={12} />
                        {t('publish')}
                      </label>
                      <div className="relative flex-1">
                        <select
                          id="privacy"
                          value={
                            visibilityMode === 'Offline' ? 'Offline' : 'Private'
                          }
                          onChange={(e) => {
                            const value = e.target.value
                            if (value === 'Private') {
                              if (videoRefImage && !isUrl(videoRefImage)) {
                                setErrorText(
                                  'This image isn’t published yet, so the video can’t be animated to Private. You can publish it after generating, or use a published image instead.',
                                )
                                setIsErrorModalOpen(true)
                                setVisibilityMode('Offline')
                                return
                              }
                              if (!isPlusOrHigher) {
                                setErrorText(
                                  'Sorry, Private mode requires an upgrade. Free users can only use Unsaved mode.',
                                )
                                setIsErrorModalOpen(true)
                                setVisibilityMode('Offline')
                              } else {
                                setVisibilityMode('Private')
                              }
                            } else {
                              setVisibilityMode('Offline')
                            }
                          }}
                          className="appearance-none w-full h-9 rounded-xl px-3 pr-9 text-xs text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition"
                        >
                          <option value="Private"> {t('private')}</option>
                          <option value="Offline"> {t('unsaved')}</option>
                        </select>
                        <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-300 pointer-events-none" />
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('selectPrivate')}
                      {!isPlusOrHigher && (
                        <>
                          {' '}
                          <Link
                            href="/upgrade"
                            className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                          >
                            {t('upgrade')}
                          </Link>
                        </>
                      )}
                    </p>
                  </div>
                )}

                {visibilityMode === 'Private' && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <label className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 w-24">
                        <FaFolder className="text-white" size={12} />
                        {t('album')}
                      </label>
                      <div className="relative flex-1">
                        <select
                          id="folder"
                          value={folderSetting}
                          onChange={(e) => setFolderSetting(e.target.value)}
                          className="appearance-none w-full h-9 rounded-xl px-3 pr-9 text-xs text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition"
                        >
                          {folders.map((folder) => (
                            <option key={folder} value={folder}>
                              {folder}
                            </option>
                          ))}
                        </select>
                        <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-300 pointer-events-none" />
                      </div>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                      {t('assignAlbum')}
                      {!isPlusOrHigher && (
                        <>
                          {' '}
                          <Link
                            href="/upgrade"
                            className="font-bold text-white hover:text-[var(--main-bg-color)] underline"
                          >
                            {t('upgrade')}
                          </Link>
                        </>
                      )}
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <div className="flex items-center gap-2">
                    <label className="text-white text-xs font-semibold flex items-center gap-1.5 shrink-0 flex-1">
                      Auto Save
                    </label>
                    <div
                      className={`relative inline-block w-12 h-6 rounded-full transition-colors duration-200 ease-out cursor-pointer shrink-0 ${
                        autoSave
                          ? 'bg-[var(--main-bg-color)] ring-1 ring-inset ring-white/15'
                          : 'bg-[var(--foreground-bg-color)]'
                      }`}
                      onClick={() => {
                        if (!user) {
                          setErrorText('Please sign in to use Auto Save.')
                          setIsErrorModalOpen(true)
                          return
                        }
                        if (isSafari) {
                          setErrorText(
                            'Auto Save is not supported on Safari. Please use Chrome/Edge/Firefox.',
                          )
                          setIsErrorModalOpen(true)
                          return
                        }
                        setAutoSave(!autoSave)
                      }}
                      role="switch"
                      aria-checked={autoSave}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 inline-block w-5 h-5 rounded-full shadow transform transition-transform duration-200 ease-out ${
                          !user
                            ? 'bg-gray-600'
                            : autoSave
                              ? 'translate-x-6 bg-white'
                              : 'translate-x-0 bg-gray-500'
                        }`}
                      />
                    </div>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                    Auto-download generated files (supported browsers only)
                  </p>
                </div>
              </>
            )}
          </>
        </div>

        {!hidePromptAndActions && (
          <div
            style={{
              background:
                'linear-gradient(0deg, rgba(15,13,20,1) 0%, rgba(15,13,20,0.4) 60%, rgba(15,13,20,0) 100%)',
              position: 'sticky',
              bottom: 0,
            }}
            className="z-20 pointer-events-none"
          >
            <div className="px-4 pt-4 pb-10 bg-[var(--middle-bg-color)] border-t border-[var(--foreground-bg-color)] pointer-events-auto">
              <div className="flex flex-wrap justify-center items-center gap-2 mb-3 text-xs">
                <div className="text-white inline-flex items-center h-7 px-3 bg-[var(--foreground-bg-color)] rounded-full">
                  <span className="mr-2 font-semibold text-gray-300 text-xs">
                    {t('cost')}
                  </span>
                  {chillMode ? (
                    <span>{t('free')}</span>
                  ) : (
                    <>
                      <FaGem className="mr-1" size={10} />
                      <span className="font-semibold text-xs">
                        {getVideoGemCost({
                          videoQuality,
                          videoVersion,
                          audioEnabled,
                          model,
                          hasReferenceImage: !!videoRefImage,
                          videoLength,
                          videoFps,
                          videoResolution,
                        })}
                      </span>
                    </>
                  )}
                </div>

                {!chillMode && (
                  <>
                    {visibilityMode === 'Offline' ? (
                      <div className="text-white inline-flex items-center h-7 px-3 bg-[var(--foreground-bg-color)] rounded-full cursor-pointer ring-1 ring-orange-300 hover:bg-[var(--secondary-bg-color)] transition-colors">
                        <IoCloudOffline className="mr-2" size={10} />
                        {t('unsaved')}
                      </div>
                    ) : (
                      <div className="text-white inline-flex items-center h-7 px-3 bg-[var(--foreground-bg-color)] rounded-full cursor-pointer ring-1 ring-[var(--main-bg-color)] hover:bg-[var(--secondary-bg-color)] transition-colors">
                        <IoCloud className="mr-2" size={10} />
                        {t('private')}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="flex items-center justify-center gap-3 w-full">
                <div className="flex-1 min-w-0">
                  <button
                    type="submit"
                    className={`w-full h-12 px-4 inline-flex justify-center items-center gap-2 rounded-full bg-[var(--main-bg-color)] text-white font-bold transition duration-200 ease-in-out hover:scale-[1.02] active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-50 disabled:cursor-not-allowed ${
                      isLoading || (!prompt && !videoRefImage)
                        ? 'opacity-50'
                        : 'opacity-100'
                    }`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <ClipLoader
                        color={'#FFF'}
                        loading={isLoading}
                        size={24}
                        aria-label={t('loadingSpinner')}
                        data-testid="loader"
                      />
                    ) : cooldown > 0 && chillMode ? (
                      <>
                        <span>{t('coolingDown')}</span>
                        <div className="w-5 h-5">
                          <CircularProgressbar
                            value={cooldownProgress}
                            styles={buildStyles({
                              textColor: '#FFFFFF',
                              pathColor: '#EEEEEE',
                              trailColor: '#7974FF',
                            })}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <FaMagic />
                        <span>
                          {videoRefImage ? t('animate') : t('create')}
                        </span>
                      </>
                    )}
                  </button>
                </div>

                {videoSrc?.length > 0 && false && (
                  /* Extend wrapper controls width */
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => setExtendModalOpen(true)}
                      disabled={isLoading}
                      className="group disabled:opacity-50 w-full"
                      type="button"
                    >
                      <div
                        className="
                  relative overflow-hidden
                  w-full px-4 py-3 rounded-full
                  bg-[var(--foreground-bg-color)]
                  outline-none ring-1 ring-gray-500
                  transition duration-200 ease-in-out
                  transform group-hover:scale-[1.02]
                  flex items-center justify-center
                "
                      >
                        {/* gradient chrome */}
                        <div
                          className="
                    absolute inset-[-2px]
                    rounded-full
                    before:absolute before:inset-0 before:rounded-full
                    before:bg-[linear-gradient(135deg,#4f547e_0%,#9a8dff_28%,#f7f6ff_50%,#9a8dff_72%,#3f446b_100%)]
                    before:shadow-[inset_0_1px_2px_rgba(255,255,255,0.55),0_0_22px_rgba(170,130,255,0.55)]
                    after:absolute after:inset-0 after:rounded-full
                    after:mix-blend-screen after:opacity-70 after:blur-[0.6px]
                    after:bg-[conic-gradient(from_210deg,transparent_0deg,rgba(255,255,255,0)_40deg,rgba(255,255,255,0.85)_55deg,rgba(220,190,255,0.65)_64deg,rgba(255,255,255,0)_78deg,transparent_120deg,transparent_360deg)]
                  "
                        >
                          <div className="pointer-events-none absolute inset-0 rounded-full bg-[linear-gradient(0deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02))] opacity-35" />
                        </div>

                        {/* inner surface */}
                        <div className="absolute inset-[2px] rounded-full bg-[var(--foreground-bg-color)]" />

                        {/* content */}
                        <div className="relative z-10 flex items-center gap-2">
                          <div className="flex items-center">
                            <FaFilm style={{ fill: 'url(#metalGradient)' }} />
                            <FaAngleDoubleRight
                              className="text-sm ml-1"
                              style={{ fill: 'url(#metalGradient)' }}
                            />
                          </div>

                          <span
                            className="
                      bg-[linear-gradient(135deg,#bcb6ff,#ffffff,#9a8dff)]
                      bg-clip-text text-transparent
                      drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]
                      text-base font-bold
                    "
                          >
                            Extend
                          </span>

                          <svg width="0" height="0" aria-hidden="true">
                            <defs>
                              <linearGradient
                                id="metalGradient"
                                x1="0%"
                                y1="0%"
                                x2="100%"
                                y2="100%"
                              >
                                <stop offset="0%" stopColor="#bcb6ff" />
                                <stop offset="50%" stopColor="#ffffff" />
                                <stop offset="100%" stopColor="#9a8dff" />
                              </linearGradient>
                            </defs>
                          </svg>
                        </div>
                      </div>
                    </button>
                  </div>
                )}
              </div>
              {queueLength && videoProgress < 1 ? (
                <div className="w-full mt-4 transition-opacity duration-300 flex items-center justify-center">
                  <p className="text-gray-400 text-xs">
                    Queue position: {queueLength}
                  </p>
                </div>
              ) : (
                <div
                  className={`w-full mt-4 opacity-0 transition-opacity duration-300 ${
                    isLoading && 'opacity-100'
                  }`}
                >
                  <div className="relative w-full h-2 bg-[var(--secondary-bg-color)] rounded-full overflow-hidden">
                    <div
                      className="absolute top-0 left-0 h-2 bg-[var(--main-bg-color)] rounded-full transition-[width] duration-1000 ease-in-out"
                      style={{
                        width: `${Math.max(videoProgress, estimatedProgress)}%`,
                      }}
                    ></div>
                  </div>
                </div>
              )}
              <p className="mt-3 flex items-start justify-center gap-1.5 text-center text-[11px] leading-snug text-gray-500">
                <FaGem className="mt-[1px] shrink-0" size={9} />
                <span>{t('videoRefundNote')}</span>
              </p>
            </div>
          </div>
        )}
      </form>
    </>
  )
}

export default InputsVideo
